import styled from 'styled-components';
import { designTokens } from '../foundation/DesignTokens';

// Main container with drawer layout
export const DrawerContainer = styled.div`
  display: flex;
  flex: 1;
  height: 100%;
  min-height: 0; /* Allow flex child to shrink below content size */
  overflow: hidden;
  background: ${designTokens.colors.gray[50]};
  font-family: ${designTokens.fonts.body};

  @media (max-width: 900px) {
    flex-direction: column;
  }
`;

// Left sidebar/drawer - matching reference HTML .ds-sidebar
export const Drawer = styled.aside`
  width: 260px;
  background: #FFFFFF;
  border-right: 1px solid ${designTokens.colors.gray[200]};
  padding: 20px 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow-y: auto;

  @media (max-width: 900px) {
    width: 100%;
    border-right: none;
    border-bottom: 1px solid ${designTokens.colors.gray[200]};
    flex-direction: row;
    overflow-x: auto;
    gap: 8px;
    padding: 12px 16px;
  }
`;

// Section title in drawer - matching reference HTML .ds-sidebar-section-title
export const DrawerSectionTitle = styled.div`
  font-size: 0.75rem;
  text-transform: uppercase;
  font-weight: 700;
  color: ${designTokens.colors.gray[500]};
  letter-spacing: 0.06em;
  padding: 0 8px;
  margin-bottom: 4px;

  @media (max-width: 900px) {
    display: none;
  }
`;

// List container for drawer items - matching reference HTML .ds-sidebar-list
export const DrawerList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;

  @media (max-width: 900px) {
    flex-direction: row;
    gap: 8px;
  }
`;

// Individual drawer item/button - matching reference HTML .ds-sidebar-item
export const DrawerItem = styled.button`
  border-radius: ${designTokens.radius.md};
  padding: 8px 10px;
  font-size: 0.87rem;
  color: ${designTokens.colors.gray[700]};
  cursor: pointer;
  font-family: ${designTokens.fonts.body};
  font-weight: 400;
  text-align: left;
  border: 1px solid transparent;
  background: transparent;
  transition: all 0.15s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: ${designTokens.colors.gray[50]};
    border-color: ${designTokens.colors.gray[100]};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  ${(props) =>
    props.active &&
    `
    background: ${designTokens.colors.primary[50]};
    border-color: ${designTokens.colors.primary[100]};
    color: ${designTokens.colors.primary[600]};
    font-weight: 600;
  `}

  @media (max-width: 900px) {
    font-size: 0.8rem;
    padding: 6px 8px;
    white-space: nowrap;
  }
`;

// Dot indicator for drawer items - matching reference HTML .ds-sidebar-dot
export const DrawerDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 999px;
  background: ${(props) =>
    props.active ? designTokens.colors.primary[500] : designTokens.colors.gray[300]
  };
  flex-shrink: 0;
`;

// Main content area - matching reference HTML .ds-content
export const DrawerContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px 32px 32px;
  background: ${designTokens.colors.gray[50]};

  @media (max-width: 900px) {
    padding: 18px 16px 24px;
  }
`;

// Header for the drawer content - matching reference HTML .ds-section-header
export const DrawerContentHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 20px;

  @media (max-width: 900px) {
    flex-direction: column;
    align-items: flex-start;
    gap: ${designTokens.spacing.md};
    margin-bottom: ${designTokens.spacing.lg};
  }
`;

// Title for the content area - matching reference HTML .ds-section-title
export const DrawerContentTitle = styled.h1`
  font-family: ${designTokens.fonts.heading};
  font-weight: 800;
  font-size: 1.25rem;
  color: ${designTokens.colors.gray[900]};
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;

  @media (max-width: 900px) {
    font-size: 1.1rem;
  }
`;

// Subtitle/description for content area - matching reference HTML .ds-section-description
export const DrawerContentSubtitle = styled.p`
  font-size: 0.9rem;
  color: ${designTokens.colors.gray[500]};
  margin: 4px 0 0 0;
  line-height: 1.5;
  max-width: 620px;

  @media (max-width: 900px) {
    font-size: 0.85rem;
  }
`;

export default DrawerContainer;
